import { CardMedia, Paper, styled, Skeleton } from "@mui/material";
import { useState, useEffect, useRef, memo } from "react";
import { useAuth } from "../../contexts/AuthContext";

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: 2,
  elevation: 3,
  overflow: "hidden", // Ensure the image doesn't overflow the rounded corners
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));

const CardMediaDiagram = ({ divesiteId }) => {
  const [diagramImageUrl, setDiagramImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser, userClaims } = useAuth();
  const abortControllerRef = useRef(null);

  // Determine image size based on claims (same logic as CardMediaImages and CardMediaHero)
  const getImageSizeFromClaims = () => {
    if (!userClaims) {
      console.log(
        "CardMediaDiagram - User claims not available, defaulting to small image size."
      );
      return "small"; // Default to small
    }
    if (userClaims.isTier5User || userClaims.isTier6User) {
      console.log(
        "CardMediaDiagram - User is Tier 5 or 6, using large image size."
      );
      return "large";
    }
    if (userClaims.isTier3User || userClaims.isTier4User) {
      console.log(
        "CardMediaDiagram - User is Tier 3 or 4, using medium image size."
      );
      return "medium";
    }
    console.log(
      "CardMediaDiagram - User tier is 1 or 2 (or not matched), using small image size."
    );
    return "small";
  };

  const size = getImageSizeFromClaims();
  console.log("CardMediaDiagram - Determined image size:", size);

  useEffect(() => {
    if (!divesiteId) return;

    // Cancel any in-flight request when component unmounts or divesiteId changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchDiagramImage = async () => {
      setLoading(true);
      setError(null);
      console.log(
        `CardMediaDiagram - Fetching diagram image for divesite ID: ${divesiteId}, size: ${size}`
      );
      try {
        const token = await currentUser.getIdToken();
        console.log("CardMediaDiagram - Firebase ID token:", token);
        const response = await fetch(
          `/api/media/divesite/${divesiteId}`,
          {
            signal: abortControllerRef.current.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("CardMediaDiagram - Fetch response:", response);
        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            `CardMediaDiagram - Failed to fetch media content: ${response.status} - ${errorText}`
          );
          throw new Error(
            `CardMediaDiagram - Failed to fetch media content: ${response.status} - ${errorText}`
          );
        }
        const data = await response.json();
        console.log("CardMediaDiagram - Fetched media data:", data);
        const diagramMediaContent = data.find(
          (media) => media.tags && media.tags.includes("diagram")
        );
        console.log(
          "CardMediaDiagram - Found diagram media content:",
          diagramMediaContent
        );
        if (diagramMediaContent?.mediaUrl) {
          console.log(
            "CardMediaDiagram - Diagram media URL:",
            diagramMediaContent.mediaUrl
          );
          const imageUrl =
            diagramMediaContent.mediaUrl[size] ||
            diagramMediaContent.mediaUrl.original;
          console.log(
            "CardMediaDiagram - Setting diagram image URL:",
            imageUrl
          );
          setDiagramImageUrl(imageUrl);
        } else {
          console.log(
            "CardMediaDiagram - No diagram image found or mediaUrl is missing."
          );
          setDiagramImageUrl(null); // Ensure null if no diagram image or mediaUrl
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(
            "CardMediaDiagram - Error fetching diagram image:",
            error
          );
          setError(error);
        }
      } finally {
        setLoading(false);
        console.log(
          "CardMediaDiagram - Fetch diagram image process completed."
        );
      }
    };

    if (divesiteId) {
      fetchDiagramImage();
    }

    return () => {
      // Cleanup function to abort fetch when component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [divesiteId, size, userClaims]); // Re-fetch when divesiteId, size, or userClaims change

  if (loading) {
    return <p>Loading diagram image...</p>;
  }

  if (error) {
    return <p>Error loading diagram image: {error.message}</p>;
  }

  return (
    <StyledPaper>
      {diagramImageUrl ? (
        <CardMedia
          component="img"
          height="200"
          image={diagramImageUrl}
          alt="Divesite diagram image"
        />
      ) : (
        <p>No diagram image available yet for this divesite. Make one!!</p>
      )}
    </StyledPaper>
  );
};

export default memo(CardMediaDiagram);
export { CardMediaDiagram };
